class FreeGiftSelector extends HTMLElement {
  constructor() {
    super();
    this.threshold = parseInt(this.dataset.threshold, 10);
    this.selectedVariantId = null;
    this.isBusy = false;
    this._cartUpdateUnsubscriber = null;
    this._dragging = false;
    this._dragStartX = 0;
    this._dragScrollLeft = 0;
  }

  connectedCallback() {
    this._restoreSelection();
    this._bindEvents();
    this._initDragScroll();
  }

  disconnectedCallback() {
    if (this._cartUpdateUnsubscriber) this._cartUpdateUnsubscriber();
    document.removeEventListener('cart:updated', this._onCartUpdatedDOM);
  }

  _restoreSelection() {
    const stored = localStorage.getItem('fg_selected_variant');
    if (stored) {
      this.selectedVariantId = parseInt(stored, 10);
      this._markSelected(this.selectedVariantId);
    }
  }

  _bindEvents() {
    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      this._cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        (data) => this._handleCartUpdate(data.cart)
      );
    }

    this._onCartUpdatedDOM = (e) => {
      if (e.detail && e.detail.cart) this._handleCartUpdate(e.detail.cart);
    };
    document.addEventListener('cart:updated', this._onCartUpdatedDOM);

    this.querySelectorAll('[data-product-variant]').forEach((item) => {
      item.querySelector('.fg-select-btn').addEventListener('click', (e) => {
        if (this._wasDragging) return;
        this._selectGift(parseInt(item.dataset.productVariant, 10));
      });
    });
  }

  _initDragScroll() {
    const slider = this.querySelector('[data-fg-products]');
    if (!slider) return;

    slider.addEventListener('mousedown', (e) => {
      this._dragging = true;
      this._wasDragging = false;
      this._dragStartX = e.pageX - slider.offsetLeft;
      this._dragScrollLeft = slider.scrollLeft;
      slider.style.userSelect = 'none';
    });

    slider.addEventListener('mouseleave', () => {
      this._dragging = false;
    });

    slider.addEventListener('mouseup', () => {
      this._dragging = false;
      slider.style.userSelect = '';
    });

    slider.addEventListener('mousemove', (e) => {
      if (!this._dragging) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const delta = x - this._dragStartX;
      if (Math.abs(delta) > 5) this._wasDragging = true;
      slider.scrollLeft = this._dragScrollLeft - delta;
    });
  }

  _nonGiftTotal(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => {
      if (item.properties && item.properties['_is_free_gift'] === 'true') return sum;
      return sum + item.original_line_price;
    }, 0);
  }

  _giftInCart(cart) {
    if (!cart || !cart.items) return null;
    return (
      cart.items.find(
        (item) => item.properties && item.properties['_is_free_gift'] === 'true'
      ) || null
    );
  }

  async _handleCartUpdate(cart) {
    const total = this._nonGiftTotal(cart);
    this._updateProgress(total);

    if (total >= this.threshold) {
      this._unlockPicker();
      const gift = this._giftInCart(cart);
      if (gift) {
        this.selectedVariantId = gift.variant_id;
        localStorage.setItem('fg_selected_variant', gift.variant_id);
        this._markSelected(gift.variant_id);
      }
    } else {
      this._lockPicker();
      const gift = this._giftInCart(cart);
      if (gift) {
        await this._removeFromCart(gift.key);
        this.selectedVariantId = null;
        localStorage.removeItem('fg_selected_variant');
      }
      this._markSelected(null);
    }
  }

  _updateProgress(total) {
    const pct = Math.min((total / this.threshold) * 100, 100);
    const fill = this.querySelector('[data-fg-fill]');
    const label = this.querySelector('[data-fg-label]');
    if (fill) fill.style.width = `${pct}%`;
    if (label) {
      if (total >= this.threshold) {
        label.innerHTML = "You've unlocked a <strong>free gift!</strong> Choose below ✨";
      } else {
        const remaining = this.threshold - total;
        label.innerHTML = `Spend <strong>${this._formatMoney(remaining)}</strong> more to unlock a free gift 🎁`;
      }
    }
  }

  _formatMoney(cents) {
    const fmt =
      window.FoxTheme &&
      FoxTheme.settings &&
      FoxTheme.settings.moneyFormat;
    if (fmt && window.FoxTheme && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
      return FoxTheme.Currency.formatMoney(cents, fmt);
    }
    return `AED ${(cents / 100).toFixed(2).replace(/\.00$/, '')}`;
  }

  _unlockPicker() {
    const picker = this.querySelector('[data-fg-picker]');
    if (!picker) return;
    picker.classList.remove('fg-locked');
    picker.querySelectorAll('.fg-select-btn').forEach((btn) => {
      btn.removeAttribute('disabled');
      if (!btn.closest('.fg-product').classList.contains('fg-selected')) {
        btn.textContent = 'Select';
      }
    });
  }

  _lockPicker() {
    const picker = this.querySelector('[data-fg-picker]');
    if (!picker) return;
    picker.classList.add('fg-locked');
    picker.querySelectorAll('.fg-select-btn').forEach((btn) => {
      btn.setAttribute('disabled', '');
      btn.textContent = '🔒 Locked';
    });
  }

  _markSelected(variantId) {
    const locked = !!this.querySelector('[data-fg-picker].fg-locked');
    this.querySelectorAll('[data-product-variant]').forEach((item) => {
      const id = parseInt(item.dataset.productVariant, 10);
      const btn = item.querySelector('.fg-select-btn');
      if (id === variantId) {
        item.classList.add('fg-selected');
        if (btn) btn.textContent = 'Selected ✓';
      } else {
        item.classList.remove('fg-selected');
        if (btn) btn.textContent = locked ? '🔒 Locked' : 'Select';
      }
    });
  }

  async _selectGift(variantId) {
    if (this.isBusy) return;
    this.isBusy = true;
    this._setBusy(true);

    try {
      if (this.selectedVariantId && this.selectedVariantId !== variantId) {
        const cartData = await fetch('/cart.js').then((r) => r.json());
        const existing = this._giftInCart(cartData);
        if (existing) await this._removeFromCart(existing.key);
      }

      await this._addToCart(variantId);
      this.selectedVariantId = variantId;
      localStorage.setItem('fg_selected_variant', variantId);
      this._markSelected(variantId);
      this._showSuccess();
    } catch (err) {
      console.error('[FreeGift] Error selecting gift:', err);
    } finally {
      this.isBusy = false;
      this._setBusy(false);
    }
  }

  _setBusy(busy) {
    this.querySelectorAll('.fg-select-btn').forEach((btn) => {
      if (busy) btn.setAttribute('disabled', '');
      else btn.removeAttribute('disabled');
    });
  }

  _showSuccess() {
    const msg = this.querySelector('[data-fg-success]');
    if (!msg) return;
    msg.removeAttribute('hidden');
    clearTimeout(this._successTimer);
    this._successTimer = setTimeout(() => msg.setAttribute('hidden', ''), 2500);
  }

  async _addToCart(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { _is_free_gift: 'true' },
      }),
    });
    if (!res.ok) throw new Error('add failed');
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    return res.json();
  }

  async _removeFromCart(lineKey) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });
    if (!res.ok) throw new Error('remove failed');
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    return res.json();
  }
}

customElements.define('free-gift-selector', FreeGiftSelector);
